import { CheckCircle, Close } from "@mui/icons-material";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Stack, Typography } from "@mui/material";
import { ReactNode, useState } from "react";
import * as yup from "yup";

import { ToastContent } from "../../../../../shared/components";
import { IVFormErros, useVForm, VForm, VTextField } from "../../../../../shared/forms";
import { useToast } from "../../../../../shared/hooks";
import { IFormInstallmentCancel, IInstallmentProposal, ProposalService } from "../../../../../shared/services/api/proposal/ProposalService";
import { globalFormatCurrencyBRL } from "../../../../../shared/utils";
import { StatusInstallmentChip } from "../../../../../shared/components/StatusInstallmentChip";
import { returnMessageError } from "../../../../../shared/utils/returnMessageError";

interface ModalSetInstallmentsPaidProps {
	isOpenModal: boolean;
	isClose: () => void;
	row?: IInstallmentProposal;
	rows?: IInstallmentProposal[];
	onSuccess?: () => void;
}

type TForm = {
	obs?: string;
};

export const ModalSetInstallmentsPaid = ({ isOpenModal, isClose, row, rows, onSuccess }: ModalSetInstallmentsPaidProps) => {
	const { formRef } = useVForm();
	const toastHandler = useToast();
	const [isLoading, setIsLoading] = useState(false);

	// Filtra apenas parcelas pendentes
	const filteredRows = rows?.filter((r: IInstallmentProposal) => (r.status ?? "").toLowerCase() === "pending") ?? [];
	const filteredRow = row && (row.status ?? "").toLowerCase() === "pending" ? row : undefined;


	const formSchema: yup.SchemaOf<TForm> = yup.object().shape({
		obs: yup.string().notRequired().default(""),
	});

	const handleConfirm = async (dados: TForm) => {
		try {
			formRef.current?.setErrors({});
			const validated = await formSchema.validate(dados, { abortEarly: false });

			const ids = filteredRow ? [filteredRow.id] : filteredRows.map((r) => r.id);
			if (ids.length === 0) {
				toastHandler.present({
					type: "warning",
					position: "top-right",
					messageOrContent: <ToastContent title="Aviso" content="Nenhuma parcela pendente disponível para baixa." />,
				});
				return;
			}

			setIsLoading(true);
			let successCount = 0;

			for (const id of ids) {
				const payload: IFormInstallmentCancel = { obs: validated.obs?.trim() ?? "" };
				const result = await ProposalService.savePaymentInstallment(payload, id);
				if (!(result instanceof Error)) {
					successCount++;
				}
			}

			setIsLoading(false);

			if (successCount > 0) {
				toastHandler.present({
					type: "success",
					position: "top-right",
					messageOrContent: (
						<ToastContent
							title="Sucesso"
							content={
								successCount === 1 ? "Parcela baixada com sucesso." : `${successCount} parcelas baixadas com sucesso.`
							}
						/>
					),
				});
				isClose();
				onSuccess?.();
			} else {
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title="Erro" content="Não foi possível baixar as parcelas selecionadas." />,
				});
			}
		} catch (errors) {
			setIsLoading(false);

			if (errors instanceof yup.ValidationError) {
				const validationErrors: IVFormErros = {};
				errors.inner.forEach((error) => {
					if (!error.path) return;
					validationErrors[error.path] = error.message;
				});
				formRef.current?.setErrors(validationErrors);
			}

			const { titleMessage, contentMessage } = returnMessageError(errors);
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
			});
		}
	};

	const getInstallmentTitle = (installment: IInstallmentProposal) => {
		if (installment.typeAmount === "discount") return "Desconto";
		if (installment.typeAmount === "add") return "Acréscimo";
		return `Parcela nº ${installment.number}`;
	};

	const renderSingleInfo = (installment: IInstallmentProposal) => (
		<Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
			<Stack direction="row" alignItems="center" gap={1}>
				<Typography variant="body1" fontWeight={600}>
					{getInstallmentTitle(installment)}
				</Typography>
				<Typography variant="body1" color="text.primary">
					{globalFormatCurrencyBRL(installment.amount)}
				</Typography>
			</Stack>
			<StatusInstallmentChip status={installment.status} />
		</Stack>
	);

	const renderMultipleInfo = (count: number) => (
		<Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
			<Typography variant="body1" fontWeight={600}>
				{`${count} parcela${count > 1 ? "s" : ""} selecionada${count > 1 ? "s" : ""}`}
			</Typography>
			<Typography variant="body2" color="text.secondary">
				Somente parcelas pendentes serão baixadas.
			</Typography>
		</Stack>
	);

	const title = filteredRows.length > 1 ? "Deseja baixar as parcelas selecionadas?" : "Deseja baixar a parcela?";

	let valueInfo: ReactNode = null;
	if (filteredRow) {
		valueInfo = renderSingleInfo(filteredRow);
	} else if (filteredRows.length === 1) {
		valueInfo = renderSingleInfo(filteredRows[0]);
	} else {
		valueInfo = renderMultipleInfo(filteredRows.length);
	}

	return (
		<Dialog open={isOpenModal} onClose={isClose} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
				<Typography variant="h6" fontWeight={400} color="primary.main">
					{title}
				</Typography>
				<IconButton onClick={isClose} size="small" disabled={isLoading}>
					<Close fontSize="small" />
				</IconButton>
			</DialogTitle>

			<DialogContent sx={{ px: 4, pt: 1 }}>
				{valueInfo && (
					<Box
						sx={{
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 2,
							bgcolor: "action.hover",
							px: 2,
							py: 1.5,
							mb: 3,
						}}
					>
						{valueInfo}
					</Box>
				)}

				<VForm ref={formRef} onSubmit={handleConfirm} onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}} placeholder="">
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<VTextField name="obs" label="Observação (opcional)" fullWidth multiline rows={3} />
						</Grid>
					</Grid>
				</VForm>
			</DialogContent>

			<DialogActions sx={{ px: 4, pb: 3, pt: 1 }}>
				<Button variant="contained" color="primary" startIcon={<CheckCircle />} onClick={() => formRef.current?.submitForm()} disabled={isLoading}>
					Confirmar
				</Button>
			</DialogActions>
		</Dialog>
	);
};
